import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Status interface (from backend with transitions)
 */
export interface Status {
  id: number;
  code: string;
  color: string | null;
  isActive: boolean;
  sortOrder: number;
  allowedTransitionsClient: number[];
  allowedTransitionsDispatcher: number[];
  allowedTransitionsDriver: number[];
  isDraft: boolean;
  isCancel: boolean;
  isSearch: boolean;
  isProgress: boolean;
  isComplete: boolean;
  isFailed: boolean;
  isTerminal: boolean;
}

/**
 * StatusService
 *
 * Кешує статуси з backend в пам'яті для швидкого доступу
 * Завантажується один раз при старті додатку
 */
@Injectable({
  providedIn: 'root'
})
export class StatusService {
  private readonly apiUrl = `${environment.apiUrl}/admin/statuses/with-transitions`;
  private statuses$ = new BehaviorSubject<Status[]>([]);
  private loaded = false;

  constructor(private http: HttpClient) {}

  /**
   * Завантажити статуси з backend (викликається після логіна)
   */
  loadStatuses(): Observable<Status[] | {data: Status[]}> {
    return this.http.get<{data: Status[]}>(this.apiUrl).pipe(
      tap(response => {
        const statuses = response.data || [];
        this.statuses$.next(statuses);
        this.loaded = true;
      })
    );
  }

  /**
   * Очистити кеш статусів (при logout)
   */
  clearCache(): void {
    this.statuses$.next([]);
    this.loaded = false;
  }

  /**
   * Перевірити чи статуси завантажені
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Отримати всі статуси (Observable)
   */
  getStatuses$(): Observable<Status[]> {
    return this.statuses$.asObservable();
  }

  /**
   * Отримати всі статуси (синхронно)
   */
  getAllStatuses(): Status[] {
    return this.statuses$.value;
  }

  /**
   * Знайти статус по ID
   */
  getStatusById(id: number): Status | undefined {
    return this.statuses$.value.find(s => s.id === id);
  }

  /**
   * Знайти статус по code
   */
  getStatusByCode(code: string): Status | undefined {
    return this.statuses$.value.find(s => s.code === code);
  }

  /**
   * Знайти статус по code
   */
  getStatusByName(name: string | undefined): Status | undefined {
    if (!name) return undefined;
    return this.statuses$.value.find(s => s.code && s.code.toLowerCase() === name.toLowerCase());
  }

  /**
   * Отримати назву статусу по ID (повертає code)
   */
  getStatusName(id: number): string {
    return this.getStatusById(id)?.code || 'Unknown';
  }

  /**
   * Отримати колір статусу по ID
   */
  getStatusColor(id: number): string {
    return this.getStatusById(id)?.color || '#9E9E9E';
  }

  /**
   * Перевірити чи статус активний
   */
  isStatusActive(id: number): boolean {
    return this.getStatusById(id)?.isActive || false;
  }

  /**
   * Отримати активні статуси
   */
  getActiveStatuses(): Status[] {
    return this.statuses$.value.filter(s => s.isActive);
  }

  /**
   * Отримати дозволені переходи для статусу (dispatcher роль)
   */
  getAllowedTransitions(fromStatusId: number, role: 'client' | 'driver' | 'dispatcher' = 'dispatcher'): Status[] {
    const status = this.getStatusById(fromStatusId);
    if (!status) return [];

    let allowedIds: number[] = [];
    if (role === 'client') {
      allowedIds = status.allowedTransitionsClient || [];
    } else if (role === 'driver') {
      allowedIds = status.allowedTransitionsDriver || [];
    } else {
      allowedIds = status.allowedTransitionsDispatcher || [];
    }

    // Never offer an inactive status as a transition target - deactivated
    // statuses (e.g. driver_arriving, in_progress) must not appear in the
    // dispatcher context menu even if they are still listed in a status's
    // allowed_transitions_* array in the DB.
    return allowedIds
      .map(id => this.getStatusById(id))
      .filter((s): s is Status => s !== undefined && s.isActive !== false);
  }

  /**
   * Перевірити чи можливий перехід
   */
  canTransition(fromStatusId: number, toStatusId: number, role: 'client' | 'driver' | 'dispatcher' = 'dispatcher'): boolean {
    const allowedTransitions = this.getAllowedTransitions(fromStatusId, role);
    return allowedTransitions.some(s => s.id === toStatusId);
  }

  /**
   * Перевірити чи статус terminal (не можна змінити)
   */
  isTerminal(statusId: number): boolean {
    return this.getStatusById(statusId)?.isTerminal || false;
  }

  /**
   * Отримати статуси по категорії
   */
  getStatusesByCategory(category: 'draft' | 'search' | 'progress' | 'complete' | 'cancel' | 'failed'): Status[] {
    return this.statuses$.value.filter(s => {
      switch (category) {
        case 'draft': return s.isDraft;
        case 'search': return s.isSearch;
        case 'progress': return s.isProgress;
        case 'complete': return s.isComplete;
        case 'cancel': return s.isCancel;
        case 'failed': return s.isFailed;
        default: return false;
      }
    });
  }
}
